import { Link, Stack, mergeStyles, MessageBarType, Spinner, PrimaryButton } from '@fluentui/react';
import * as React from 'react';
import { OneButtonMessageBar } from '../../../../web/common/components/OneButtonMessageBar';
import { PageHeader } from '../../../../web/common/components/PageHeader';
import { StyledText } from '../../../../web/common/components/StyledText';
import { ServiceAgreement } from '../../../../web/contact-support/components/ServiceAgreement';
import { TagId } from '../../../../web/models/getHelp/GetHelpLoggingRequest';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { addGenevaLog } from '../../../../web/utils/statelessUtil';
import { getString } from '../../../../web/utils/stringLoader';
import { OutdatedDevicesAlertEnabledComponent, SourceApps, URLs } from '../../../models/constants/constants';
import { sanitizeHTML } from '../../../utilities';
import { AgentModalityOptionContainer } from '../containerComponents/AgentModalityOptionContainer';
import { OutdatedDevicesAlertContainer } from '../containerComponents/OutdatedDevicesAlertContainer';
import { RegionalSettingsContainer } from '../containerComponents/RegionalSettingsContainer';
import {
  ModalityResponseModel,
  NonAgentModality,
  NonAgentModalityType,
  WaitTimeResponseModel,
} from '../models/modalityModels';
import { ExtensionAttributeKeys, FreelanceRequest } from '../models/requestModels';
import { commonClassNames } from '../styles/commonStyles';
import { getChatModalityButtonStyles, modalitySectionTitleStyles } from '../styles/modalityStyles';
import { getContactUsClassNames, IContactUsStyles } from '../styles/contactUsStyles';
import { IStyledComponent } from '../styles/themesUI';
import {
  NonAgentModalityStandard,
  NonAgentModalityPromotion,
  NonAgentModalityDescriptionOnly,
  NonAgentModalityRedirectUrl,
  NonAgentModalityOEM,
  NonAgentModalityOemBox,
  NonAgentModalityStandardBox,
  NonAgentModalityRedirectUrlBox,
  NonAgentModalityDescriptionOnlyBox,
  AskCommunityBox,
  NonAgentModalityAMCAskCommunity,
} from './NonAgentModalityOption';
import { capturePageAction } from '../../../../web/utils/oneDsUtil';
import { useSelector } from 'react-redux';
import { marketSelector } from '../../app/selectors/contactUsSelector';

export interface ModalityOptionsProps {
  isGettingAvailableModalities: boolean;
  modalityResponse: ModalityResponseModel;
  isGetHelp: boolean;
  isSignUp: boolean;
  isDarkTheme: boolean;
  isAmc: boolean;
  isAMCModalityEnabled: boolean;
  alchemyTheme: string;
  showGetCurrentBox: boolean;
  request: FreelanceRequest;
  recommendationModality: NonAgentModality;
  showAgentModalitiesButtonClicked: boolean;
  correlationId: string;
  isTeams: boolean;

  dispatchFetchAvailableModalitiesV2(): void;
  updateDraftRequest: (properties: Partial<FreelanceRequest>) => void;
  articleOnClickHandler: (url: string) => {};
  dispatchUpdateShowAgentModalitiesButton: (showAgentModalitiesButtonClicked: boolean) => void;
}

type IModalityOptionsProps = ModalityOptionsProps & IStyledComponent<IContactUsStyles>;

export const ModalityOptions = (props: IModalityOptionsProps): JSX.Element => {
  React.useEffect(() => {
    getHelpLogger.logTicketingPageLoaded('ModalitiesListPage');
  }, []);

  let serviceAgreementLink: string = null;

  if (!!getString('modality_serviceAgreementMessageText', true, true, true)) {
    const culture = useSelector(marketSelector);
    serviceAgreementLink = getString('modality_serviceAgreementMessageText')
      .replace('{0}', `<a id='serviceAgreementLink' href=${URLs.ServiceAgreementLink.replace('{0}', culture)} target='_blank'>`)
      .replace('{1}', '</a>');
  }

  const createServiceAgreementMarkup = () => {
    return sanitizeHTML(serviceAgreementLink, ['target']);
  };

  let nonAgentModalitiesStandard: NonAgentModality[];
  let nonAgentModalitiesPromotion: NonAgentModality[];
  let nonAgentModalitiesDescriptionOnly: NonAgentModality[];
  let nonAgentModalitiesRedirectUrl: NonAgentModality[];
  let nonAgentModalitiesOEM: NonAgentModality[];
  let nonAgentModalitiesAMCAskCommunity: NonAgentModality[];

  const contactUsClassNames = getContactUsClassNames(props.styles, {
    theme: props.theme,
    isSignUp: props.isSignUp,
    showAgentModalitiesButtonClicked: props.showAgentModalitiesButtonClicked,
  });
  const chatModalityButtonStyles = getChatModalityButtonStyles({
    theme: props.theme,
    withBorderRadius: props.isGetHelp,
  });

  if (
    !!props.modalityResponse &&
    !!props.modalityResponse.NonAgentModalities &&
    !!props.modalityResponse.NonAgentModalities.Modalities &&
    props.modalityResponse.NonAgentModalities.Modalities.length > 0
  ) {
    const nonAgentModalities: NonAgentModality[] = props.modalityResponse.NonAgentModalities.Modalities;
    nonAgentModalitiesStandard = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.Standard
    );
    nonAgentModalitiesPromotion = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.Promotion
    );
    nonAgentModalitiesDescriptionOnly = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.DescriptionOnly
    );
    nonAgentModalitiesRedirectUrl = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.RedirectURL
    );
    nonAgentModalitiesOEM = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.OEM
    );
    nonAgentModalitiesAMCAskCommunity = nonAgentModalities.filter(
      (nonAgentModality: NonAgentModality) => nonAgentModality.Type === NonAgentModalityType.AMCAskCommunity
    );
  }

  if (nonAgentModalitiesPromotion?.length > 0) {
    getHelpLogger.logPromotionsModalityReceived(nonAgentModalitiesPromotion[0].Link);
  }

  const oemSection =
    !!nonAgentModalitiesOEM &&
    nonAgentModalitiesOEM.length > 0 &&
    nonAgentModalitiesOEM.map((nonAgentModality: NonAgentModality) =>
      props.isGetHelp ? (
        <NonAgentModalityOemBox
          nonAgentModality={nonAgentModality}
          key={nonAgentModality.Link}
          theme={props.theme}
          alchemyTheme={props.alchemyTheme}
        />
      ) : (
        <NonAgentModalityOEM nonAgentModality={nonAgentModality} key={nonAgentModality.Link} theme={props.theme} />
      )
    );

  const handleSignUpModalityButtonClicked = (isMouseClick: boolean) => {
    props.dispatchUpdateShowAgentModalitiesButton(true);
    const logHandlerTags = {
      content: {
        contentId: 'stickyFooterAgentModalitySignUpBtn',
        contentName: 'stickyFooterAgentModalitySignUp',
        formnm: 'maxFunnel',
        fileName: 'ModalityOptions.tsx',
        methodName: 'handleSignUpModalityButtonClicked',
        areaName: 'modalitiesSection',
      },
      actionType: isMouseClick ? 'CL' : 'KE',
    };
    capturePageAction(null, logHandlerTags);
  };

  const mainSection = (
    <>
      {!!props.modalityResponse &&
        !!props.modalityResponse.AgentModalities &&
        !!props.modalityResponse.AgentModalities.Modalities && (
          <>
            {props.isSignUp && (
              <div
                className={mergeStyles(
                  contactUsClassNames.modalityDescription,
                  contactUsClassNames.wrapText,
                  contactUsClassNames.signUpDescription
                )}
                id="signUpSupportOptionDescription"
              >
                {getString('result_signUpPageTalkToMicrosoftAdvisorDescription')}
              </div>
            )}
            {(!props.isSignUp || props.showAgentModalitiesButtonClicked) &&
              props.modalityResponse.AgentModalities.Modalities.map((agentModality: WaitTimeResponseModel) => (
                <AgentModalityOptionContainer agentModality={agentModality} key={agentModality.Modality} />
              ))}
          </>
        )}
      {!!nonAgentModalitiesStandard &&
        nonAgentModalitiesStandard.length > 0 &&
        nonAgentModalitiesStandard.map((nonAgentModality: NonAgentModality) =>
          props.isGetHelp ? (
            <NonAgentModalityStandardBox
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
              articleOnClickHandler={props.articleOnClickHandler}
              isGetHelp={props.isGetHelp}
              isDarkTheme={props.isDarkTheme}
              alchemyTheme={props.alchemyTheme}
            />
          ) : (
            <NonAgentModalityStandard
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
            />
          )
        )}
      {!!nonAgentModalitiesRedirectUrl &&
        nonAgentModalitiesRedirectUrl.length > 0 &&
        nonAgentModalitiesRedirectUrl.map((nonAgentModality: NonAgentModality) =>
          props.isGetHelp ? (
            <NonAgentModalityRedirectUrlBox
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
              alchemyTheme={props.alchemyTheme}
            />
          ) : (
            <NonAgentModalityRedirectUrl
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
            />
          )
        )}
      {!!nonAgentModalitiesDescriptionOnly &&
        nonAgentModalitiesDescriptionOnly.length > 0 &&
        nonAgentModalitiesDescriptionOnly.map((nonAgentModality: NonAgentModality) =>
          props.isGetHelp ? (
            <NonAgentModalityDescriptionOnlyBox
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
              articleOnClickHandler={props.articleOnClickHandler}
              isGetHelp={props.isGetHelp}
              isDarkTheme={props.isDarkTheme}
              alchemyTheme={props.alchemyTheme}
            />
          ) : (
            <NonAgentModalityDescriptionOnly
              nonAgentModality={nonAgentModality}
              key={nonAgentModality.Link}
              theme={props.theme}
            />
          )
        )}
      {!!nonAgentModalitiesAMCAskCommunity &&
        nonAgentModalitiesAMCAskCommunity.length > 0 &&
        props.isAMCModalityEnabled &&
        nonAgentModalitiesAMCAskCommunity.map((nonAgentModality: NonAgentModality) => (
          <NonAgentModalityAMCAskCommunity nonAgentModality={nonAgentModality} theme={props.theme} />
        ))}
      {(!props.modalityResponse || (!!props.modalityResponse && !!props.modalityResponse.ShowAskCommunity)) &&
        (props.isGetHelp ? (
          <AskCommunityBox theme={props.theme} />
        ) : (
          <div
            className={mergeStyles(
              'col-lg-12',
              contactUsClassNames.col,
              contactUsClassNames.bodyCopySmall,
              contactUsClassNames.modalityOption,
              contactUsClassNames.wrapText
            )}
          >
            <Link
              className={contactUsClassNames.modalityOptionTitle}
              id="communityForumLink"
              data-bi-bhvr="COMMUNITY"
              data-bi-area="modalitiesSection"
              data-bi-name="communityForumLink"
              data-bi-formnm="maxFunnel"
              href={URLs.CommunityLink}
              target="_blank"
            >
              {getString('modality_communityLinkMessageText')}
            </Link>
          </div>
        ))}
    </>
  );

  const footerSection = (
    <>
      {props.isSignUp && !props.showAgentModalitiesButtonClicked && (
        <div
          id="signUpFooter"
          className={mergeStyles(
            commonClassNames.textNormal,
            contactUsClassNames.signUpFooter,
            contactUsClassNames.stickyFooterFixed,
            commonClassNames.positionFixed
          )}
        >
          <PrimaryButton
            id={`Agent_Modality_SignUp_Button`}
            styles={chatModalityButtonStyles}
            tabIndex={0}
            data-bi-bhvr="CONTACT"
            data-bi-area="modalitiesSection"
            data-bi-formnm="maxFunnel"
            data-bi-name={`Agent_Modality_SignUp_Button`}
            onClick={() => handleSignUpModalityButtonClicked(true)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                handleSignUpModalityButtonClicked(false);
              }
            }}
          >
            {getString('modality_chatTextV2')}
          </PrimaryButton>
        </div>
      )}
    </>
  );

  const promotionSection =
    !!nonAgentModalitiesPromotion &&
    nonAgentModalitiesPromotion.length > 0 &&
    nonAgentModalitiesPromotion.map((nonAgentModality: NonAgentModality) => (
      <NonAgentModalityPromotion
        dispatchFetchAvailableModalitiesV2={props.dispatchFetchAvailableModalitiesV2}
        updateDraftRequest={props.updateDraftRequest}
        nonAgentModality={nonAgentModality}
        key={nonAgentModality.Link}
        theme={props.theme}
      />
    ));

  const outdatedDevicesAlert = !props.isGetHelp && (
    <OutdatedDevicesAlertContainer callingComponent={OutdatedDevicesAlertEnabledComponent.ModalitySection} />
  );

  let osUpdateContent: JSX.Element = null;
  const [isOsUpdateBarVisible, setOsUpdateBarVisibility] = React.useState(true);
  if (props.showGetCurrentBox) {
    const alchemyTheme = props.request?.ExtensionAttributes[ExtensionAttributeKeys.Theme];
    const themeSelection = [
      props.request?.ExtensionAttributes[ExtensionAttributeKeys.ThemeL1],
      props.request?.ExtensionAttributes[ExtensionAttributeKeys.ThemeL2],
      props.request?.ExtensionAttributes[ExtensionAttributeKeys.ThemeL3],
    ];

    const onClickInstallOsUpdateButton = (link: string) => () => {
      getHelpLogger.logGetCurrentEvent(TagId.GetCurrent_AsModality_InstallButtonClicked, alchemyTheme, themeSelection);
      addGenevaLog({
        eventName: 'GetCurrent_AsModality_InstallButtonClicked',
        correlationId: props.correlationId,
        alchemyTheme: alchemyTheme,
        themeSelection: themeSelection,
        behavior: 'CLICK',
      });
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      window.open(link);
    };

    const onCloseOsUpdateBar = () => {
      getHelpLogger.logGetCurrentEvent(TagId.GetCurrent_AsModality_CloseButtonClicked, alchemyTheme, themeSelection);
      addGenevaLog({
        eventName: 'GetCurrent_AsModality_CloseButtonClicked',
        correlationId: props.correlationId,
        alchemyTheme: alchemyTheme,
        themeSelection: themeSelection,
        behavior: 'CLICK',
      });
      setOsUpdateBarVisibility(false);
    };
    getHelpLogger.logGetCurrentEvent(TagId.GetCurrent_AsModality_NotificationDisplayed, alchemyTheme, themeSelection);
    addGenevaLog({
      eventName: 'GetCurrent_AsModality_NotificationDisplayed',
      correlationId: props.correlationId,
      alchemyTheme: alchemyTheme,
      themeSelection: themeSelection,
      behavior: 'DISPLAY',
    });
    osUpdateContent = (
      <OneButtonMessageBar
        isTeams={props.isTeams}
        content={props.recommendationModality.Title}
        buttonText={props.recommendationModality.Description}
        messageBarType={MessageBarType.error}
        onButtonClick={onClickInstallOsUpdateButton(props.recommendationModality.Link)}
        onDismiss={onCloseOsUpdateBar}
        styles={{ root: { marginBottom: 20 } }}
      />
    );
  }

  const body = (
    <>
      {props.isGettingAvailableModalities ? (
        <div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall)}>
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Spinner styles={{ circle: { color: props.theme.palette.accent } }} />
            <span>{getString('modality_gettingAvailableModalitiesText')}</span>
          </Stack>
        </div>
      ) : (
        <>
          {props.isGetHelp ? (
            <Stack tokens={{ childrenGap: 28 }}>
              {!!oemSection && (
                <Stack tokens={{ childrenGap: 9 }}>
                  <ModalitiesSectionTitle title={getString('ModalitiesPageOEMTitle')} />
                  {oemSection}
                </Stack>
              )}
              {!!mainSection && (
                <Stack tokens={{ childrenGap: 9 }}>
                  <ModalitiesSectionTitle title={getString('ModalitiesPageMicrosoftTitle')} />
                  {mainSection}
                </Stack>
              )}
              {!!promotionSection && (
                <Stack tokens={{ childrenGap: 9 }}>
                  <ModalitiesSectionTitle title={getString('ModalitiesPagePayOptionTitle')} />
                  {promotionSection}
                </Stack>
              )}
              <ServiceAgreement theme={props.theme} />
            </Stack>
          ) : (
            <>
              {mainSection}
              {footerSection}
              {promotionSection}
              <RegionalSettingsContainer />
              {(!props.isSignUp || (props.isSignUp && props.showAgentModalitiesButtonClicked)) && (
                <div
                  className={mergeStyles(
                    'col-lg-12',
                    contactUsClassNames.col,
                    contactUsClassNames.bodyCopySmall,
                    contactUsClassNames.serviceAgreementMessage,
                    commonClassNames.overflowVisible
                  )}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={createServiceAgreementMarkup()}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      {outdatedDevicesAlert}
      {isOsUpdateBarVisible && osUpdateContent}
      <PageHeader
        title={props.isAmc ? getString('amcStrings_chooseSupportOption') : getString('modality_chooseSupportOption')}
        theme={props.theme}
        isOutlookModalityOptionHeader={true}
      />
      <div className={contactUsClassNames.panelBody}>{body}</div>
    </>
  );
};

const ModalitiesSectionTitle = (props: { title: string }): JSX.Element => (
  <StyledText styles={modalitySectionTitleStyles} tabIndex={-1} role="heading" aria-label={props.title}>
    {props.title}
  </StyledText>
);
